import { useCallback, useRef, useState } from "react";
import { UnionServiceType } from "src/api/adminMemo/adminmemo-types";
import { DetailTotalData } from "src/api/contract/contract-types";
import { findDefaultClosingPolicy, flattenClosingPolicy, formatClosingPeriodLabelWithWeek, formatClosingTimeLabel } from "../utils/closingTime";
import { findDefaultReservationPolicy } from "../utils/reservationPolicy";
import { BaseButton } from "src/components";
import useGetAvailableReservationTime from "src/hooks/product/useGetAvailableReservationTime";
import useGetClosingTimePolicy from "src/hooks/product/useGetClosingTimePolicy";
import useGetReservationPolicy from "src/hooks/product/useGetReservationPolicy";
import useGetUnitReservationTime from "src/hooks/product/useGetUnitReservationTime";

import { SpaceTypeLabel, SpaceTypeT } from "src/pages/product/product-types";
import { useModal } from "src/recoil/modalState/hook";
import { convertMinutesToDHMS } from "../libs";
import DefaultSettingModal from "../modal/DefaultSettingModal";
import UsageMonthlyTimeModal from "../modal/UsageMonthlyTimeModal";

export const ContractTimeMonthMap = {
  MEETING_ROOM: "meetingRoomTotalTime",
  DESK: "deskTotalTime",
  REFRESH_ROOM: "refreshRoomTotalTime",
} as const;

export const ContractTimeDayMap = {
  MEETING_ROOM: "meetingRoomDailyTime",
  DESK: "deskDailyTime",
  REFRESH_ROOM: "refreshRoomDailyTime",
} as const;

interface Props {
  spaceType: SpaceTypeT;
  serviceType: UnionServiceType;
  serviceId: string;
  isEditable: boolean;


  //아래부터 신청계약 전용
  detailData?: DetailTotalData;
}

const ReservationPolicyView = ({ spaceType, serviceType, serviceId, isEditable, detailData }: Props) => {
  //기본설정만 보여주는 화면
  const { setAbstractModalZ1, setBaseModal } = useModal();
  const [isOpen, setIsOpen] = useState(false);
  const [scrollLeft, setScrollLeft] = useState(false);
  const [scrollEnd, setScrollEnd] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setScrollLeft(el.scrollLeft > 0);
    setScrollEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1);
  }, []);

  // 예약 가능 기간 (RES03)
  const { data: availableReservationTimeList } = useGetAvailableReservationTime({
    AvailableReservationTimeListParams: { isDefault: true, serviceId, serviceType, commonFacilityType: spaceType },
  });

  // RRVP03/09 usage-limit-policy (이용한도)
  const isProductPage = serviceType === "SERVICE_PRODUCT";
  const { data: usageLimitPolicyList } = useGetReservationPolicy({
    owner: isProductPage ? { productId: serviceId } : { contractManageId: serviceId },
    policyType: "usage-limit-policy",
  });

  const defaultPolicy = findDefaultReservationPolicy(usageLimitPolicyList, "usage-limit-policy", spaceType);
  const usageLimitPolicy = defaultPolicy?.details?.usageLimitPolicy;

  const { data: unitReservationTimeList } = useGetUnitReservationTime({
    unitReservationTimeListParams: { isDefault: true, serviceId, serviceType, commonFacilityType: spaceType },
  });

  const { data: closingTimePolicyList } = useGetClosingTimePolicy({
    owner: serviceType === "SERVICE_PRODUCT" ? { productId: serviceId } : { contractManageId: serviceId },
  });

  // 기본 정책(공용공간 타입 전체에 적용)의 휴무 목록
  const closedPeriodList = flattenClosingPolicy(findDefaultClosingPolicy(closingTimePolicyList, spaceType));

  const TimeStart = convertMinutesToDHMS(availableReservationTimeList?.[0]?.availableReservationTimeStart);
  const TimeEnd = convertMinutesToDHMS(availableReservationTimeList?.[0]?.availableReservationTimeEnd);

  const minTime = convertMinutesToDHMS(unitReservationTimeList?.[0]?.minimumReservationTime);
  const maxTime = convertMinutesToDHMS(unitReservationTimeList?.[0]?.maximumReservationTime);

  return (
    <div>
      {/* 공간 추가 버튼 */}
      <div className="flex-center-between mt20">
        <div className="d-flex flex-column gap10 pt20">
          <p className="font-weight-semibold font15">{SpaceTypeLabel[spaceType]} 예약 기본 정책</p>
          <p className="font14 text-primary3">• 기본 정책은 모든 {SpaceTypeLabel[spaceType]}에 적용됩니다. 아래 목록에서 각 {SpaceTypeLabel[spaceType]}별로 개별 설정을 관리할 수 있습니다.</p>
        </div>
        <div className="flex-center">
          {/* {serviceType !== "SERVICE_PRODUCT" && (
            //신청계약만
            <BaseButton
              title={"월 사용 시간 확인"}
              className="ml10 color-white"
              onClick={() => {
                setAbstractModalZ1({
                  size: "large",
                  isOpen: true,
                  children: <UsageMonthlyTimeModal spaceType={spaceType} contractId={detailData?.contract?.contractId!}></UsageMonthlyTimeModal>,
                });
              }}
            />
          )} */}
          <BaseButton
            disabled={
              detailData?.contract?.contractStep === "APPLY_CANCEL" ||
              detailData?.contract?.contractStep === "TERMINATE_COMPLETE" ||
              detailData?.contract?.contractStep === "APPLY_CONFIRM" ||
              detailData?.contract?.contractStep === "USE_COMPLETE"
            }
            title={"기본 정책 설정"}
            className="ml10 color-white"
            onClick={() => {
              setAbstractModalZ1({
                size: "xlarge",
                isOpen: true,
                children: (
                  <DefaultSettingModal
                    spaceType={spaceType}
                    serviceType={serviceType}
                    serviceId={serviceId}
                    // isEditable={isEditable}
                    isDefault={true}
                    detailData={detailData}
                  ></DefaultSettingModal>
                ),
              });
            }}
          />
        </div>
      </div>

      <div className={`gray-board-default-wrapper${scrollLeft ? " show-left" : ""}${scrollEnd ? " hide-right" : ""}`}>
        <div className="gray-board-default" ref={scrollRef} onScroll={handleScroll}>
          <div className="wrap minmax120">
            <div className="title">예약 가능 기간</div>
            <div className="text">
              {!availableReservationTimeList?.[0]?.isLimitedAvailableReservationTime ? (
                "무제한"
              ) : (
                <div>
                  {TimeStart && <span>{TimeStart} 이후</span>}
                  {TimeStart && TimeEnd && " - "}
                  {TimeEnd && <span>{TimeEnd} 이내</span>}
                </div>
              )}
            </div>
          </div>
          <div className="wrap minmax120">
            <div className="title">예약 시간 단위</div>
            <div className="text">
              {!unitReservationTimeList?.[0]?.isLimitedUnitReservationTime ? (
                "무제한"
              ) : (
                <div>
                  {minTime && <span>최소{minTime}</span>}
                  {minTime && maxTime && " - "}
                  {maxTime && <span>최대{maxTime}</span>}
                </div>
              )}
            </div>
          </div>
          <div className="wrap minmax360">
            <div className="title">휴무일</div>
            {isOpen === false &&
              (closedPeriodList && closedPeriodList?.length > 0
                ? closedPeriodList?.slice(0, 3)?.map((data) => {
                  return (
                    <div key={data.id} className="text">
                      <span>
                        {formatClosingPeriodLabelWithWeek(data)} ({formatClosingTimeLabel(data)})
                      </span>
                    </div>
                  );
                })
                : "-")}
            {isOpen === true &&
              closedPeriodList?.map((data) => {
                return (
                  <div key={data.id} className="text">
                    <span>
                      {formatClosingPeriodLabelWithWeek(data)} ({formatClosingTimeLabel(data)})
                    </span>
                  </div>
                );
              })}
            {closedPeriodList && closedPeriodList.length > 3 && (
              <div onClick={() => setIsOpen((prev) => !prev)} className={`curser-pointer  ${isOpen ? "ic_chevron_up_b" : "ic_chevron_down_b"}`}></div>
            )}
          </div>
          <div className="wrap minmax120">
            <div className="title">월 이용한도 (시간)</div>
            <div className="text">
              <span>
                {usageLimitPolicy?.disabled || !usageLimitPolicy?.time || !usageLimitPolicy?.time?.monthlyLimit || usageLimitPolicy?.time?.monthlyLimit?.disabled
                  ? "무제한"
                  : convertMinutesToDHMS(usageLimitPolicy.time.monthlyLimit.minutes) ?? "-"}
              </span>
            </div>
          </div>
          <div className="wrap minmax120">
            <div className="title">일 이용한도 (시간)</div>
            <div className="text">
              <span>
                {usageLimitPolicy?.disabled || !usageLimitPolicy?.time || !usageLimitPolicy?.time?.dailyLimit || usageLimitPolicy?.time?.dailyLimit?.disabled
                  ? "무제한"
                  : convertMinutesToDHMS(usageLimitPolicy.time.dailyLimit.minutes) ?? "-"}
              </span>
            </div>
          </div>
          <div className="wrap minmax120">
            <div className="title">월 이용한도 (요금)</div>
            <div className="text">
              <span>
                {usageLimitPolicy?.disabled || !usageLimitPolicy?.fee || !usageLimitPolicy?.fee?.monthlyLimit || usageLimitPolicy?.fee?.monthlyLimit?.disabled
                  ? "무제한"
                  : `${Number(usageLimitPolicy.fee.monthlyLimit.amount ?? 0).toLocaleString()}원`}
              </span>
            </div>
          </div>
          <div className="wrap minmax120">
            <div className="title">일 이용한도 (요금)</div>
            <div className="text">
              <span>
                {usageLimitPolicy?.disabled || !usageLimitPolicy?.fee || !usageLimitPolicy?.fee?.dailyLimit || usageLimitPolicy?.fee?.dailyLimit?.disabled
                  ? "무제한"
                  : `${Number(usageLimitPolicy.fee.dailyLimit.amount ?? 0).toLocaleString()}원`}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReservationPolicyView;
